/**
 * @flow
 * @format
 */
/* eslint-disable jsx-a11y/no-static-element-interactions */

import React from 'react';
import { connect } from 'react-redux';
import _ from 'lodash';
import { pick } from 'lodash/fp';
import cc from 'classcat';
import { Col } from 'reactstrap';

import { getFieldValues, getDirtyFields } from '~/selectors/design';
import { HashId, createHTMLMarkup, branch } from '~/utils';

// import log from '~/utils/macros/debug.macro';
import type { Connector } from 'react-redux';

import RevertInput from '../RevertInput';

// import fieldWrapperClasses from '../FieldWrapper/FieldWrapper.scss';
import classes from './TableSelectionGroup.scss';

type OwnPropsType = {
  changed: boolean,
  changeInput(payload: any): void,
  defaultVal: number | string | null,
  error: string[],
  dataTarget: any,
  id: string,
  name: string | ReactElementType,
  revertInput(payload: any): void,
  tooltip: string | ReactElementType,
  value: number | string | null,
  warning: string[],
};

type PropsType = {
  dirtyVal: string,
  pos: number,
  rowData: string[][],
  rowHeader: string[],
  showClear: boolean,
} & OwnPropsType;

const hashId = new HashId();

function generateLink(part) {
  return (
    <a
      href={`/PowerSolutions/product.do?id=${part}`}
      rel="noopener noreferrer"
      target="_blank"
    >
      {part}
    </a>
  );
}

// eslint-disable-next-line react/prefer-stateless-function
export class TableSelectionGroup extends React.Component<PropsType, *> {
  static defaultProps = {
    rowHeader: [],
    rowData: [],
    error: [],
    warning: [],
  };

  props: PropsType;

  shouldComponentUpdate(nextProps: PropsType) {
    const subProps = pick([
      'changed',
      'defaultVal',
      'dirtyVal',
      'error',
      'evaluating',
      'pos',
      'value',
      'warning',
      'rowData',
    ]);

    return !_.isEqual(subProps(this.props), subProps(nextProps));
  }

  render() {
    if (!_.isArray(this.props.rowHeader)) return null;

    const hasError = this.props.error.length > 0;
    const hasWarning = this.props.warning.length > 0;

    const tHeader = this.props.rowHeader.map((s: string) => {
      const spaceIndex = s.lastIndexOf(' ');
      const title =
        spaceIndex === -1
          ? s
          : `${s.slice(0, spaceIndex)}</br>${s.slice(spaceIndex + 1)}`;
      return (
        <th className="sticky-top" key={s}>
          {createHTMLMarkup(title)}
        </th>
      );
    });

    // Quick fix for #853616
    const linkOrNot = (str: string) => {
      if (this.props.id !== 'SuitableInductors') {
        return generateLink(str);
      }
      return str;
    };

    const tRows = this.props.rowData.map((r: string[], k: number) => {
      const id = r[0];
      const onChange = () => this.props.changeInput({ target: { value: id } });
      const isSelected = k === this.props.pos;
      const isDirty = id === this.props.dirtyVal;

      return (
        <tr
          key={id}
          className={cc([
            {
              [classes.success]: isSelected,
              [classes.selected]: isDirty,
            },
            classes.partRow,
          ])}
          onClick={onChange}
        >
          <td>
            <input
              type="radio"
              checked={isSelected || isDirty}
              onChange={onChange}
            />
          </td>
          {r.map((d, l) => (
            <td key={hashId.encode(`${k}${l}`)}>
              {l === 0 ? linkOrNot(d) : d}
            </td>
          ))}
        </tr>
      );
    });

    const hasData = this.props.rowData.length > 0;
    const isEstLoss = ['Losscomp_diode', 'Losscomp_FET'].includes(
      this.props.id,
    );

    const getClearButton = () => {
      if (this.props.showClear) {
        return (
          <button
            className="btn btn-link btn-sm"
            onClick={this.props.revertInput}
          >
            Clear previously selected part
          </button>
        );
      }
      return null;
    };

    const getContents = () => {
      if (hasError) {
        return (
          <div className="p-2">
            <p>Error: {this.props.error}</p>
            <p>{getClearButton()}</p>
          </div>
        );
      }

      if (hasWarning) {
        return <div className="p-2">{this.props.warning}</div>;
      }

      const colSpan = tHeader.length - 1; // One of the th is added below

      return (
        <div className={cc([classes.scrollable])}>
          <table className={cc(['table', classes.tableFixed])}>
            <thead>
              {branch(
                hasData && isEstLoss,
                <tr>
                  <th
                    colSpan="2"
                    style={{
                      border: 0,
                      backgroundColor: 'white',
                    }}
                  />
                  <th
                    colSpan={colSpan}
                    style={{ border: 0, textAlign: 'center' }}
                  >
                    V<sub>in</sub> = V<sub>in</sub> MIN
                  </th>
                </tr>,
              )}
              <tr>
                <th className="sticky-top" />
                {tHeader}
              </tr>
            </thead>
            <tbody>{tRows}</tbody>
          </table>
        </div>
      );
    };

    const canRevert =
      this.props.changed || this.props.value !== this.props.defaultVal;

    const recommendedDiv = (
      <RevertInput
        // className={fieldWrapperClasses.inputGroupDefault}
        value={this.props.defaultVal}
        enable={canRevert}
        onClick={this.props.revertInput}
      />
    );

    return (
      <Col>
        <div className="pt-2 pl-2 pr-2 d-flex">
          {this.props.name}
          {this.props.tooltip}
        </div>
        {branch(
          isEstLoss && !hasError,
          <div className="pb-2 pl-2">Please select part below</div>,
        )}
        {getContents()}
        {branch(
          !hasError,
          <div className={classes.recommended}>{recommendedDiv}</div>,
        )}
      </Col>
    );
  }
}

function mapStateToProps(
  state: AppStateType,
  ownProps: OwnPropsType,
): PropsType {
  const values = getFieldValues(state);
  const dirtyValues = getDirtyFields(state);

  const id = ownProps.dataTarget || ownProps.id;
  const data = values[id];
  const dirty = dirtyValues[id];

  const tableData = _.get(data, ['currentValue'], []);
  const rowData = _.tail(tableData);
  const pos = _.findIndex(rowData, o => o[0] === ownProps.value);

  // Check if this is a clear part condition
  let showClear = true;
  if (
    dirtyValues.Sel_fet_pkg === undefined &&
    dirtyValues.Sel_diode_pkg === undefined
  ) {
    showClear = false;
  }

  return {
    ...ownProps,
    rowData,
    rowHeader: _.head(tableData),
    dirtyVal: dirty,
    showClear,
    pos,
  };
}

const connector: Connector<OwnPropsType, PropsType> = connect(mapStateToProps);

export default connector(TableSelectionGroup);



// WEBPACK FOOTER //
// ./src/components/TableSelectionGroup/TableSelectionGroup.js